import classnames from 'classnames';
import { useId } from 'react';
import { Icon } from '@/atoms/Icons/index.js';
import { useValue } from '@/hooks/useValue.js';
import '../controls.css';
import type { FormControlProps } from '@/molecules/Forms/FormControl';
import { useInternalStatus } from '@/hooks/useInternalStatus';

export interface CheckboxProps<Value = string> extends FormControlProps<Value> {
  size?: number;
  onChange?: (name: string, value: Value | null) => void;
}

export function Checkbox<Value = string>(props: CheckboxProps<Value>) {
  const internalId = useId();

  const {
    id = internalId,
    name = id,
    className,
    dataTestId,
    disabled = false,
    checked: initialChecked = false,
    value,
    size = 24,
    tabIndex = 0,
    validationStatus = 'default',
    errorMessage,
    children,
    onChange,
    ...rest
  } = props;

  const { value: checked, setValue } = useValue<boolean>({
    name,
    initialValue: initialChecked,
    onChange(name, checked) {
      onChange?.(name, checked ? ((value !== undefined ? value : checked) as Value) : null);
    },
  });
  const internalStatus = useInternalStatus({
    isDisabled: disabled,
    validationStatus,
  });

  const shouldDisplayErrorMessage = internalStatus === 'error' && errorMessage;

  return (
    <div
      className={classnames('flex flex-col space-y-2', className)}
      data-testid={`checkbox-container-${id}`}
    >
      <label
        className={classnames('cursor-pointer relative flex text-b3 items-center', {
          'text-grey': internalStatus === 'disabled',
          'text-black': internalStatus !== 'default',
        })}
      >
        <span className="relative self-start me-8">
          <input
            {...rest}
            name={name}
            data-testid={dataTestId}
            type="checkbox"
            tabIndex={tabIndex}
            onChange={() => {
              !disabled && setValue(!checked);
            }}
            checked={checked}
            data-name="Checkbox"
            disabled={disabled}
            value={value as any}
          />

          <span
            style={{ height: size, width: size }}
            className={classnames({ '!border-red': internalStatus === 'error' })}
            data-testid={`check-container-${id}`}
          >
            <Icon
              name="CheckDefault"
              type="svg"
              width={`${size}px`}
              color="black"
              className="absolute"
            />
          </span>
        </span>

        {children}
      </label>

      {shouldDisplayErrorMessage && (
        <span className="text-red text-b4 flex items-start space-x-4 ps-20" role="alert">
          <Icon name="Error" width="20px" />
          {errorMessage}
        </span>
      )}
    </div>
  );
}
